import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
	Flex,
	Box,
	useBreakpointValue,
	useColorModeValue,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
} from '@chakra-ui/react';

import Sidebar from '../sidebarNav/SideNavBar';
import TodoForm from '../TodoTask/TodoForm';
import TopLayoutHeader from './TopLayoutHeader';
import GlobalUsageLimitsBar from './GlobalUsageLimitsBar';
import PageContent from './PageContent';
import FEATURE_BARS from './featureBarsConfig';
import brandColor from '../../styles/Brand';
import defaultSizes from '../../utils/defaultSizes';
import AuthenticatedFooter from './AuthenticatedFooter';
import SupportWidget from '../Support/SupportWidget';

const LoggedInLayout = ({
	pageTitle = 'Ops Pilot CRM',
	pageDescription = '',
	companyId,
	employeeId,
	children,
	showPageHeader = true,
	showSidebar = true,
}) => {
	const [isCollapsed, setIsCollapsed] = useState(() => {
		const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
		if (w < 992) return false; // mobile — sidebar hidden entirely, collapse state irrelevant
		if (w < 1200) return true; // medium viewport — start collapsed
		return JSON.parse(localStorage.getItem('sidebarCollapsed')) || false;
	});
	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined' ? window.innerWidth < 992 : false,
	);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);

	const prevWidthRef = useRef(
		typeof window !== 'undefined' ? window.innerWidth : 1200,
	);

	// Persist collapse preference
	useEffect(() => {
		localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
	}, [isCollapsed]);

	// Responsive resize handler
	useEffect(() => {
		const handleResize = () => {
			const w = window.innerWidth;
			const prev = prevWidthRef.current;
			prevWidthRef.current = w;

			// Mobile breakpoint (<992)
			if (w < 992 && prev >= 992) {
				setIsMobile(true);
				setIsMobileOpen(false);
			} else if (w >= 992 && prev < 992) {
				setIsMobile(false);
			}

			// Auto collapse/expand at 1200px (non-mobile only)
			if (w >= 992 && prev >= 992) {
				if (prev >= 1200 && w < 1200) {
					// Crossing below 1200 → auto-collapse
					setIsCollapsed(true);
				} else if (prev < 1200 && w >= 1200) {
					// Crossing above 1200 → auto-expand
					setIsCollapsed(false);
				}
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const showModal = () => setIsModalVisible(true);
	const handleCloseModal = () => setIsModalVisible(false);

	// Responsive header layout values
	const headerText = useColorModeValue(
		brandColor.systemDarkBlue,
		brandColor.white,
	);
	// const sidebarBg = useColorModeValue('#009688', '#009688');
	// const sidebarBackgroundColor =
	// 	companySettings?.data?.settings?.branding?.sidebarBackgroundColor ||
	// 	'white';

	const topHeaderBg = useColorModeValue('#fdffffff', '#ffffffff');

	const location = useLocation();
	const isBuilderPage =
		location.pathname.startsWith('/builder/email') ||
		(/^\/landing-pages\/[^/]+$/.test(location.pathname) &&
			location.pathname !== '/landing-pages') ||
		location.pathname.startsWith('/automations/create') ||
		location.pathname.startsWith('/automations/edit/');

	// Check if GlobalUsageLimitsBar will render anything
	const hasVisibleBars = FEATURE_BARS.some((bar) =>
		bar.matchPaths.some((p) =>
			bar.exactMatch ? location.pathname === p : location.pathname.startsWith(p),
		),
	);

	return (
		<Flex
			w='100%'
			minH='100vh'
			position='relative'>
			{/* Mobile backdrop overlay */}
			{isMobile && isMobileOpen && (
				<Box
					position='fixed'
					inset='0'
					bg='blackAlpha.600'
					zIndex={199}
					onClick={() => setIsMobileOpen(false)}
				/>
			)}

			{showSidebar && (
				// Clip wrapper — animates width to slide sidebar in/out on mobile
				// and handles collapse width on desktop. overflow:hidden prevents
				// any absolutely-positioned children (e.g. collapse button) from
				// bleeding outside the sidebar boundary.
				<Box
					position='fixed'
					top='0'
					left='0'
					h='100vh'
					overflowX='hidden'
					w={
						isMobile
							? isMobileOpen
								? defaultSizes.sidebarWidth
								: '0'
							: isCollapsed
								? defaultSizes.sidebarCollapsedWidth
								: defaultSizes.sidebarWidth
					}
					transition='width 0.25s ease'
					zIndex={isMobile ? 200 : 120}>
					<Box
						boxShadow='md'
						as='aside'
						h='100vh'
						w={
							isCollapsed && !isMobile
								? defaultSizes.sidebarCollapsedWidth
								: defaultSizes.sidebarWidth
						}
						minW={
							isCollapsed && !isMobile
								? defaultSizes.sidebarCollapsedWidth
								: defaultSizes.sidebarWidth
						}
						color={brandColor.white}
						transition='width 0.2s ease'>
						<Sidebar
							onCollapse={setIsCollapsed}
							externalCollapsed={isMobile ? false : isCollapsed}
							isMobile={isMobile}
							onMobileClose={() => setIsMobileOpen(false)}
						/>
					</Box>
				</Box>
			)}
			<Flex
				direction='column'
				flex='1'
				minW={0}
				minH='100vh'
				h={isBuilderPage ? '100vh' : undefined}
				overflow={isBuilderPage ? 'hidden' : undefined}
				position='relative'
				w={
					isMobile
						? '100%'
						: isCollapsed
							? `calc(100% - ${defaultSizes.sidebarCollapsedWidth})`
							: `calc(100% - ${defaultSizes.sidebarWidth})`
				}
				ml={
					isMobile
						? '0'
						: isCollapsed
							? `${defaultSizes.sidebarCollapsedWidth}`
							: `${defaultSizes.sidebarWidth}`
				}>
			
				{/* main body content */}
				<Box
					as='main'
					display='flex'
					flexDir='column'
					flex='1'
					w='100%'
					minH={0}
					overflow={isBuilderPage ? 'hidden' : undefined}>
			
					<TopLayoutHeader
						isMobile={isMobile}
						onMobileMenuOpen={() => setIsMobileOpen(true)}
						pageTitle={pageTitle}
						pageDescription={pageDescription}
						showModal={showModal}
						companyId={companyId}
						employeeId={employeeId}
						topHeaderBg={topHeaderBg}
						headerText={headerText}
					/>


					{isBuilderPage ? (
						// Builder: fixed viewport shell; only the canvas scrolls internally.
						<Flex
							w='100%'
							direction='column'
							flex='1'
							minH={0}
							overflow='hidden'>
							{hasVisibleBars && <GlobalUsageLimitsBar />}
							<Box flex='1' minH={0} overflow='hidden' display='flex' flexDirection='column'>
								{children}
							</Box>
						</Flex>
					) : (
						<PageContent>
							<Box
								w='100%'
								pt={0}
								pr={defaultSizes.contentPaddingRight}
								pb={defaultSizes.contentPaddingBottom}
								pl={defaultSizes.contentPaddingLeft}>
							
								{children}
							</Box>
						</PageContent>
					)}
				</Box>

				{!isBuilderPage && <AuthenticatedFooter />}

				{/* Modal */}
				<Modal isOpen={isModalVisible} onClose={handleCloseModal} size='xl'>
					<ModalOverlay />
					<ModalContent maxW={'1000px'} borderRadius='md' boxShadow='2xl'>
						<ModalHeader>Create a New Todo</ModalHeader>
						<ModalCloseButton />
						<ModalBody p={6}>
							<TodoForm
								onSave={handleCloseModal}
								onCancel={handleCloseModal}
								showTitle={false}
							/>
						</ModalBody>
						<ModalFooter />
					</ModalContent>
				</Modal>
			</Flex>
			<SupportWidget />
		</Flex>
	);
};

export default LoggedInLayout;
